/* `/apps` — the Composio catalog, the accounts a user connects to it, the
   agents those accounts are linked to, and the triggers an agent subscribes to.
   CONTRACT §6 and §7 for the wire, §11 for the rules this file follows.

   In `shared/api/` for the same reason as `knowledge.ts`: three features read
   it — the Apps page, the agent Configure panel and the left sidebar's app
   stack — and a feature may never import a feature (rule book §1 rule 5).

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes
   through `unwrap`. */
import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type {
  AgentAppLink,
  AppCategory,
  AppConnection,
  AppDetail,
  AppsPage,
  AppsQuery,
  ConnectBody,
  ConnectionLink,
  ConnectionLinkCreate,
  ConnectionLinkPatch,
  ConnectionPatch,
  ConnectionRevoked,
  ConnectionUnlinked,
  ConnectStarted,
  TriggerCreate,
  TriggerDeleted,
  TriggerPatch,
  TriggerSubscription,
} from "@/shared/types";

export const appsKeys = {
  all: ["apps"] as const,
  list: (params: AppsQuery) => [...appsKeys.all, "list", params] as const,
  categories: () => [...appsKeys.all, "categories"] as const,
  detail: (slug: string) => [...appsKeys.all, "detail", slug] as const,
  connections: (agentId?: string) => [...appsKeys.all, "connections", agentId ?? null] as const,
  links: (connectionId: string) => [...appsKeys.all, "links", connectionId] as const,
  agentLinks: (agentId: string) => [...appsKeys.all, "agent-links", agentId] as const,
  triggers: (agentId: string) => [...appsKeys.all, "triggers", agentId] as const,
};

/* ------------------------------ catalog ---------------------------------- */

export async function listApps(params: AppsQuery = {}): Promise<AppsPage> {
  return unwrap(await client.get<Envelope<AppsPage>>("/apps", { params }));
}

export async function listAppCategories(): Promise<AppCategory[]> {
  return unwrap(await client.get<Envelope<AppCategory[]>>("/apps/categories"));
}

/** The row plus its `spec` and the connections I can see — the detail page. */
export async function getApp(slug: string): Promise<AppDetail> {
  return unwrap(await client.get<Envelope<AppDetail>>(`/apps/${slug}`));
}

/** Starts the hosted consent flow and answers where to send the browser. */
export async function connectApp(slug: string, body: ConnectBody = {}): Promise<ConnectStarted> {
  return unwrap(await client.post<Envelope<ConnectStarted>>(`/apps/${slug}/connect`, body));
}

/* ---------------------------- connections -------------------------------- */

/** With `agentId`, only the connections linked to that agent, each with `link`. */
export async function listConnections(agentId?: string): Promise<AppConnection[]> {
  return unwrap(
    await client.get<Envelope<AppConnection[]>>("/apps/connections", { params: { agent_id: agentId } }),
  );
}

/** The poll target of the connect flow. */
export async function getConnection(id: string): Promise<AppConnection> {
  return unwrap(await client.get<Envelope<AppConnection>>(`/apps/connections/${id}`));
}

export async function patchConnection(id: string, body: ConnectionPatch): Promise<AppConnection> {
  return unwrap(await client.patch<Envelope<AppConnection>>(`/apps/connections/${id}`, body));
}

/** Revoked at Composio first, then soft-deleted here with its links. */
export async function deleteConnection(id: string): Promise<ConnectionRevoked> {
  return unwrap(await client.delete<Envelope<ConnectionRevoked>>(`/apps/connections/${id}`));
}

export async function listConnectionLinks(id: string): Promise<ConnectionLink[]> {
  return unwrap(await client.get<Envelope<ConnectionLink[]>>(`/apps/connections/${id}/agents`));
}

export async function linkConnection(id: string, body: ConnectionLinkCreate): Promise<ConnectionLink> {
  return unwrap(await client.post<Envelope<ConnectionLink>>(`/apps/connections/${id}/agents`, body));
}

export async function patchConnectionLink(
  id: string,
  agentId: string,
  body: ConnectionLinkPatch,
): Promise<ConnectionLink> {
  return unwrap(await client.patch<Envelope<ConnectionLink>>(`/apps/connections/${id}/agents/${agentId}`, body));
}

export async function unlinkConnection(id: string, agentId: string): Promise<ConnectionUnlinked> {
  return unwrap(await client.delete<Envelope<ConnectionUnlinked>>(`/apps/connections/${id}/agents/${agentId}`));
}

/* --------- an app on an agent without an owner's connection ---------------- */

/** Every app on the agent, whatever account it uses. */
export async function listAgentLinks(agentId: string): Promise<AgentAppLink[]> {
  return unwrap(await client.get<Envelope<AgentAppLink[]>>("/apps/links", { params: { agent_id: agentId } }));
}

/** The app through each customer's own account (`customer`), or with none (`none`). */
export async function setAppSource(slug: string, agentId: string, account: "customer" | "none"): Promise<AgentAppLink> {
  return unwrap(await client.put<Envelope<AgentAppLink>>(`/apps/${slug}/agents/${agentId}`, { account }));
}

export async function removeAppSource(slug: string, agentId: string): Promise<void> {
  await client.delete(`/apps/${slug}/agents/${agentId}`);
}

/* ------------------------------ triggers --------------------------------- */

export async function listTriggers(agentId: string): Promise<TriggerSubscription[]> {
  return unwrap(await client.get<Envelope<TriggerSubscription[]>>("/apps/triggers", { params: { agent_id: agentId } }));
}

export async function createTrigger(connectionId: string, body: TriggerCreate): Promise<TriggerSubscription> {
  return unwrap(
    await client.post<Envelope<TriggerSubscription>>(`/apps/connections/${connectionId}/triggers`, body),
  );
}

export async function patchTrigger(id: string, body: TriggerPatch): Promise<TriggerSubscription> {
  return unwrap(await client.patch<Envelope<TriggerSubscription>>(`/apps/triggers/${id}`, body));
}

export async function deleteTrigger(id: string): Promise<TriggerDeleted> {
  return unwrap(await client.delete<Envelope<TriggerDeleted>>(`/apps/triggers/${id}`));
}

/* ------------------------------- hooks ----------------------------------- */

export function useApps(params: AppsQuery = {}) {
  return useQuery({ queryKey: appsKeys.list(params), queryFn: () => listApps(params) });
}

export function useAppCategories() {
  return useQuery({ queryKey: appsKeys.categories(), queryFn: listAppCategories });
}

export function useApp(slug: string | null) {
  return useQuery({
    queryKey: appsKeys.detail(slug ?? ""),
    queryFn: () => getApp(slug as string),
    enabled: !!slug,
  });
}

export function useConnections(agentId?: string) {
  return useQuery({
    queryKey: appsKeys.connections(agentId),
    queryFn: () => listConnections(agentId),
  });
}

export function useConnectionLinks(connectionId: string | null) {
  return useQuery({
    queryKey: appsKeys.links(connectionId ?? ""),
    queryFn: () => listConnectionLinks(connectionId as string),
    enabled: !!connectionId,
  });
}

export function useAgentLinks(agentId: string) {
  return useQuery({
    queryKey: appsKeys.agentLinks(agentId),
    queryFn: () => listAgentLinks(agentId),
    enabled: !!agentId,
  });
}

export function useTriggers(agentId: string | null) {
  return useQuery({
    queryKey: appsKeys.triggers(agentId ?? ""),
    queryFn: () => listTriggers(agentId as string),
    enabled: !!agentId,
  });
}

/* One prefix for every write: connecting an app flips `connected` on a catalog
   row, adds a connection, and can change what an agent's trigger list resolves
   against. Invalidate wide, narrow only when it is measurably slow. */
function useAppsMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: appsKeys.all }) });
}

export const usePatchConnection = () =>
  useAppsMutation((v: { id: string; body: ConnectionPatch }) => patchConnection(v.id, v.body));

export const useDeleteConnection = () => useAppsMutation(deleteConnection);

export const useLinkConnection = () =>
  useAppsMutation((v: { id: string; body: ConnectionLinkCreate }) => linkConnection(v.id, v.body));

export const usePatchConnectionLink = () =>
  useAppsMutation((v: { id: string; agentId: string; body: ConnectionLinkPatch }) =>
    patchConnectionLink(v.id, v.agentId, v.body),
  );

export const useSetAppSource = () =>
  useAppsMutation((v: { slug: string; agentId: string; account: "customer" | "none" }) =>
    setAppSource(v.slug, v.agentId, v.account),
  );

export const useRemoveAppSource = () =>
  useAppsMutation((v: { slug: string; agentId: string }) => removeAppSource(v.slug, v.agentId));

export const useUnlinkConnection = () =>
  useAppsMutation((v: { id: string; agentId: string }) => unlinkConnection(v.id, v.agentId));

export const useCreateTrigger = () =>
  useAppsMutation((v: { connectionId: string; body: TriggerCreate }) => createTrigger(v.connectionId, v.body));

export const usePatchTrigger = () =>
  useAppsMutation((v: { id: string; body: TriggerPatch }) => patchTrigger(v.id, v.body));

export const useDeleteTrigger = () => useAppsMutation(deleteTrigger);

/* --------------------------- the connect flow ---------------------------- */

const POLL_INTERVAL_MS = 2_000;
const MAX_POLLS = 60;
/* How many more polls to allow once the consent window is gone. A grant that
   landed a beat before the close still needs to arrive, but somebody who
   cancelled should not hold the button for the full two minutes. */
const GRACE_POLLS = 3;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Connect an app through Composio's hosted page, then wait for the grant.
 *
 * The popup is opened blank *inside the click* — a window opened after an await
 * is a pop-up blocker's definition of unsolicited — and gets its URL once the
 * backend answers. The poll is what decides the outcome: Composio records the
 * grant server-side, so a closed window is not an answer and the loop keeps
 * going for `GRACE_POLLS` after it.
 *
 * Returns the connected account, or `null` on every way out that is not a
 * grant — so a "connect & add" caller can link the row it just authorized
 * without guessing which one it is.
 */
export function useConnectApp() {
  const qc = useQueryClient();
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const running = useRef(false);

  const connect = useCallback(
    async (slug: string, body: ConnectBody = {}): Promise<AppConnection | null> => {
      if (running.current) return null;
      running.current = true;
      setBusySlug(slug);

      const width = 600;
      const height = 720;
      const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
      const grant = window.open("", "definable-connect", `popup,width=${width},height=${height},left=${left},top=${top}`);

      try {
        // A blocked popup answers null. Said plainly here rather than left to
        // throw on the assignment below — same way out, a message to act on.
        if (!grant) throw new Error("The browser blocked the window we open the provider in.");
        const started = await connectApp(slug, body);
        grant.location.href = started.redirect_url;

        let sinceClosed = 0;
        for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
          await sleep(POLL_INTERVAL_MS);
          const connection = await getConnection(started.id);
          if (connection.status === "connected") {
            grant.close();
            toast.success(`${connection.app.name} connected`);
            return connection;
          }
          if (connection.status === "failed") throw new Error("The provider rejected the connection.");
          if (grant.closed && (sinceClosed += 1) >= GRACE_POLLS) {
            throw new Error("The window closed before the connection finished.");
          }
        }
        throw new Error("Timed out waiting for the provider.");
      } catch (err) {
        // Every way out closes it; close() on an already-shut window is a no-op.
        grant?.close();
        console.error(err);
        toast.error(err instanceof Error ? err.message : "Could not connect this app.");
        return null;
      } finally {
        running.current = false;
        setBusySlug(null);
        qc.invalidateQueries({ queryKey: appsKeys.all });
      }
    },
    [qc],
  );

  return { connect, busySlug };
}
